import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { BTN_COLOR, BTN_TEXT_COLOR, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import { menuTranslationSchema, getSupportedLanguages } from '../schemas/menu-translation-schema';
import type { TMenuTranslationFields, TMenuTranslation } from '../schemas';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';
import { useGetMenuTranslation, useCreateMenuTranslation } from '../services/translation-service';

// ─── English preview (reads live form values via context) ─────────────────────

const EnglishPreview = () => {
  const { watch } = useFormContext<TMenuItemFormValues>();
  const [name, description, allergies, nutritions, dishnote] = watch([
    'name',
    'description',
    'allergies',
    'nutritions',
    'dishnote',
  ]);

  const fields = [
    { label: 'Name', value: name },
    { label: 'Description', value: description },
    { label: 'Allergies', value: allergies },
    { label: 'Nutritions', value: nutritions },
    { label: 'Dish Notes', value: dishnote },
  ];

  return (
    <Box sx={s.previewCard}>
      <Typography sx={s.previewTitle}>Original (English)</Typography>
      <Divider sx={{ borderColor: THEME_COLOR4, mb: 1.5 }} />
      {fields.map(({ label, value }) => (
        <Box key={label} sx={s.previewRow}>
          <Typography sx={s.previewLabel}>{label}</Typography>
          {value ? (
            <Typography sx={s.previewValue}>{value}</Typography>
          ) : (
            <Typography sx={s.previewEmpty}>Not set</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

// ─── Translation form (re-mounts on language change via key) ──────────────────

type TTranslationFormProps = {
  restaurantId: string;
  menuItemId: string;
  selectedLang: string;
  languageName: string;
  existingTranslation: TMenuTranslation | null | undefined;
};

const TranslationFormFields = ({
  restaurantId,
  menuItemId,
  selectedLang,
  languageName,
  existingTranslation,
}: TTranslationFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TMenuTranslationFields>({
    resolver: zodResolver(menuTranslationSchema),
    defaultValues: {
      name: existingTranslation?.name ?? '',
      description: existingTranslation?.description ?? '',
      allergies: existingTranslation?.allergies ?? '',
      nutritions: existingTranslation?.nutritions ?? '',
      dishnote: existingTranslation?.dishnote ?? '',
    },
  });

  const createMutation = useCreateMenuTranslation();
  const [saved, setSaved] = useState(false);
  const isSaving = createMutation.isPending;

  const onSubmit = (data: TMenuTranslationFields) => {
    createMutation.mutate(
      { restaurantId, menuItemId, translationData: { lang: selectedLang, ...data } },
      {
        onSuccess: () => {
          reset(data);
          setSaved(true);
        },
      }
    );
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <TextField
        label="Name *"
        size="small"
        fullWidth
        {...register('name')}
        error={!!errors.name}
        helperText={errors.name?.message}
        InputLabelProps={s.inputLabelProps}
        sx={s.field}
      />
      <TextField
        label="Description"
        size="small"
        fullWidth
        multiline
        rows={2}
        {...register('description')}
        InputLabelProps={s.inputLabelProps}
        sx={s.field}
      />
      <TextField
        label="Allergies"
        size="small"
        fullWidth
        multiline
        rows={2}
        {...register('allergies')}
        InputLabelProps={s.inputLabelProps}
        sx={s.field}
      />
      <TextField
        label="Nutritions"
        size="small"
        fullWidth
        multiline
        rows={2}
        {...register('nutritions')}
        InputLabelProps={s.inputLabelProps}
        sx={s.field}
      />
      <TextField
        label="Dish Notes"
        size="small"
        fullWidth
        multiline
        rows={2}
        {...register('dishnote')}
        InputLabelProps={s.inputLabelProps}
        sx={s.field}
      />

      <Button
        type="submit"
        variant="contained"
        disabled={!isDirty || isSaving}
        sx={s.saveBtn}
      >
        {isSaving ? (
          <>
            <CircularProgress size={14} sx={{ color: BTN_TEXT_COLOR, mr: 1 }} />
            Saving…
          </>
        ) : (
          `Save ${languageName} Translation`
        )}
      </Button>

      {/* Success alert — visible only while form is clean (no new edits) */}
      {saved && !isDirty && (
        <Alert
          severity="success"
          onClose={() => setSaved(false)}
          sx={s.alert}
        >
          {languageName} translation saved
        </Alert>
      )}

      {createMutation.isError && (
        <Alert severity="error" sx={s.alert}>
          {createMutation.error instanceof Error
            ? createMutation.error.message
            : 'Failed to save translation'}
        </Alert>
      )}
    </Box>
  );
};

// ─── Dialog ───────────────────────────────────────────────────────────────────

type TMenuTranslationDialogProps = {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  menuItemId: string;
  configuredLanguages: string[];
};

const MenuTranslationDialog = ({
  open,
  onClose,
  restaurantId,
  menuItemId,
  configuredLanguages,
}: TMenuTranslationDialogProps) => {
  const allLanguages = useMemo(() => getSupportedLanguages(), []);
  const [selectedLang, setSelectedLang] = useState(configuredLanguages[0] ?? '');

  useEffect(() => {
    if (open) setSelectedLang(configuredLanguages[0] ?? '');
  }, [open, configuredLanguages]);

  const languageName = allLanguages.find((l) => l.code === selectedLang)?.name ?? selectedLang;

  const { data: existingTranslation, isLoading } = useGetMenuTranslation(
    restaurantId,
    menuItemId,
    selectedLang,
    open && !!selectedLang
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={s.title}>Translations</DialogTitle>

      <DialogContent dividers sx={s.content}>
        <Grid container spacing={3}>
          {/* Left: English preview */}
          <Grid item xs={12} sm={5}>
            <EnglishPreview />
          </Grid>

          {/* Right: language select + form */}
          <Grid item xs={12} sm={7}>
            <Typography sx={s.sectionLabel}>Translate to</Typography>
            <Select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
              fullWidth
              size="small"
              sx={s.languageSelect}
            >
              {configuredLanguages.map((code) => (
                <MenuItem key={code} value={code} sx={s.menuItem}>
                  {allLanguages.find((l) => l.code === code)?.name ?? code}
                </MenuItem>
              ))}
            </Select>

            {isLoading ? (
              <Box sx={s.loadingBox}>
                <CircularProgress size={24} sx={{ color: BTN_COLOR }} />
              </Box>
            ) : (
              <TranslationFormFields
                key={selectedLang}
                restaurantId={restaurantId}
                menuItemId={menuItemId}
                selectedLang={selectedLang}
                languageName={languageName}
                existingTranslation={existingTranslation}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={s.closeBtn}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MenuTranslationDialog;

const s = {
  title: { fontFamily: 'GilroyBold', fontSize: 16, pb: 1 },
  content: { p: 2.5 },
  previewCard: {
    borderRadius: '10px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FAFAFA',
    p: 2,
    height: '100%',
  },
  previewTitle: { fontFamily: 'GilroySemiBold', fontSize: 13, color: '#000', mb: 1.5 },
  previewRow: { mb: 1.5 },
  previewLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 11,
    color: THEME_COLOR3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    mb: 0.25,
  },
  previewValue: { fontFamily: 'GilroyRegular', fontSize: 13, color: '#000' },
  previewEmpty: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: THEME_COLOR3,
    fontStyle: 'italic',
  },
  sectionLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    color: THEME_COLOR3,
    mb: 0.75,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  languageSelect: {
    mb: 2.5,
    borderRadius: '10px',
    fontFamily: 'GilroyRegular',
    fontSize: 13,
  },
  menuItem: { fontFamily: 'GilroyRegular', fontSize: 13 },
  inputLabelProps: { style: { fontFamily: 'GilroyRegular', fontSize: 13 } },
  field: {
    '& .MuiInputBase-root': { borderRadius: '10px' },
    '& .MuiInputBase-input': { fontFamily: 'GilroyRegular', fontSize: 13 },
  },
  saveBtn: {
    backgroundColor: BTN_COLOR,
    color: BTN_TEXT_COLOR,
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    borderRadius: '8px',
    height: 40,
    mt: 0.5,
    transition: 'opacity 0.2s ease',
    '&:hover': { backgroundColor: BTN_COLOR },
    '&.Mui-disabled': { opacity: 0.65, color: BTN_TEXT_COLOR, backgroundColor: BTN_COLOR },
  },
  alert: { borderRadius: '8px', fontFamily: 'GilroyRegular', fontSize: 13 },
  loadingBox: { display: 'flex', justifyContent: 'center', py: 5 },
  closeBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    color: '#000',
    borderRadius: '8px',
  },
};
